import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { STATUS_CHANGED_TIMESTAMP } from '../../common/constants';
import messages from '../../common/messages';
import type { OnSort, OnPageChange, OnFilterChange } from '../../common/types';
import FetchPlansService from '../../services/fetch-plans';
import {
	PAGE_LIMIT,
	DEFAULT_SORT_DIRECTION,
	PLAN_DIRECTORY_ERROR_FLAG_ID,
	NO_JSW_ACCESS_FOR_USER,
} from './constants';
import type { Props, State, OnSortData } from './types';

export default function TableController(props: Props) {
	const { status } = props;
	const { showFlag } = useFlagsService();

	const [tableState, setTableState] = useState<State>({
		page: 1,
		sortField: STATUS_CHANGED_TIMESTAMP,
		sortDirection: DEFAULT_SORT_DIRECTION,
		filter: {
			text: '',
		},
		status,
	});
	const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
	const pageRef = useRef(0);
	const { page, sortField, sortDirection, filter } = tableState;

	useEffect(() => {
		if (errorCode === NO_JSW_ACCESS_FOR_USER) {
			showFlag({
				id: PLAN_DIRECTORY_ERROR_FLAG_ID,
				type: 'error',
				title: messages.inactivePlansDirectoryNoAccessPermissionTitle,
				description: messages.inactivePlansDirectoryNoAccessPermissionDescriptionSpork,
			});
		}
	}, [showFlag, errorCode]);

	const onPageChange: OnPageChange = useCallback(
		(_, pageNumber) => {
			setTableState({ ...tableState, page: pageNumber });
		},
		[tableState],
	);

	const refetchPlans = (): void => {
		pageRef.current = 0;
		setTableState({ ...tableState, page: 1 });
	};

	const onSort: OnSort = useCallback(
		(data: OnSortData, analyticsEvent?: UIAnalyticsEvent) => {
			pageRef.current = 0;
			setTableState({
				...tableState,
				page: 1,
				sortField: data.key,
				sortDirection: data.sortOrder,
			});
			if (analyticsEvent) {
				fireUIAnalytics(analyticsEvent, 'tableHeader', { key: data.key });
			}
		},
		[tableState],
	);

	const onFilterChange: OnFilterChange = useCallback(
		(newFilters) => {
			pageRef.current = 0;
			setTableState({
				...tableState,
				page: 1,
				filter: {
					...tableState.filter,
					...newFilters,
				},
			});
		},
		[tableState],
	);

	return (
		<FetchPlansService status={status}>
			{({ loading, data, error, errorDetails, fetch }) => {
				if (error) {
					setErrorCode(errorDetails);
					return null;
				}
				if (!loading && pageRef.current !== page) {
					pageRef.current = page;
					fetch(tableState);
				}

				return props.children({
					isLoading: loading,
					data,
					page,
					limit: PAGE_LIMIT,
					onPageChange,
					onSort,
					sortField,
					sortDirection,
					refetchPlans,
					onFilterChange,
					filter,
				});
			}}
		</FetchPlansService>
	);
}
